import React from 'react';
import { Pressable, View } from 'react-native';

import i18n from '@/utils/i18n';
import { Avatar, Badge, createAvatarProps } from '@usepepper/sprout-kit-ui';
import { createStyles } from '@/contexts/theme-context';
import { EmployeeRole, EmployeeStatus } from '@/types';
import { formatPhoneNumber } from '@/utils/phone-number';
import { RestaurantEmployee } from '@sales-portal/domain/accounts/screens/employee-list/types';
import { Typography } from '@usepepper/ui/atoms/typography';

const getBadgeLabel = (employee: RestaurantEmployee): string => {
  if (employee.employee_status === EmployeeStatus.Invited) {
    return i18n.t('sales_portal.employee_list.invited');
  }
  return employee.restaurant_employee_role === EmployeeRole.Admin
    ? i18n.t('sales_portal.employee_list.admin')
    : i18n.t('sales_portal.employee_list.user');
};

interface EmployeeListItemProps {
  employee: RestaurantEmployee;
  onPress: (employee: RestaurantEmployee) => void;
}

export const EmployeeListItem: React.FC<EmployeeListItemProps> = ({
  employee,
  onPress,
}) => {
  const [styles, theme] = useStyles();

  const {
    employee_contact_email: contactEmail,
    employee_contact_phone_number: phoneNumber,
    employee_id: employeeId,
    employee_name: name,
  } = employee;

  const subtitle = phoneNumber ? formatPhoneNumber(phoneNumber) : contactEmail;

  return (
    <Pressable
      accessibilityLabel={i18n.t('sales_portal.employee_list.row_label', {
        name,
      })}
      accessibilityRole="button"
      onPress={() => onPress(employee)}
      style={({ hovered }) => [
        styles.container,
        hovered ? styles.hovered : undefined,
      ]}
    >
      <Avatar {...createAvatarProps(name, employeeId, 2)} size="$sm" />
      <View style={styles.infoColumn}>
        <Typography numberOfLines={1} variant="paragraph-md-600">
          {name}
        </Typography>
        {subtitle ? (
          <Typography
            color={theme.sk.color.text.tertiary}
            numberOfLines={1}
            variant="paragraph-sm"
          >
            {subtitle}
          </Typography>
        ) : null}
      </View>

      <View style={styles.badgeWrapper}>
        <Badge color="muted">{getBadgeLabel(employee)}</Badge>
      </View>
    </Pressable>
  );
};

const useStyles = createStyles((theme) => ({
  badgeWrapper: {
    alignSelf: 'center',
  },
  container: {
    alignItems: 'center',
    borderBottomColor: theme.sk.color.border.primary,
    borderBottomWidth: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSMedium,
    justifyContent: 'space-between',
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  hovered: {
    backgroundColor: theme.sk.color.bg.primary.hover,
  },
  infoColumn: {
    flex: 1,
  },
}));
